import React, { useRef, useState } from 'react';
import Head from 'next/head';
import { Grid } from '@mui/material';
import { useRouter } from 'next/router';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import HistoryIcon from '@mui/icons-material/History';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import InfoIcon from '@mui/icons-material/Info';
import BuildIcon from '@mui/icons-material/Build';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

import {
  TabsV2,
  CommentsV2,
  ActivityListV2,
  TaskCarDetails,
  RepairsForm,
  TaskDetailScrollableSection,
} from '@components';
import {
  UploadARNMImageV2,
  UploadARNMDocumentV2,
} from 'components/common/media';
import { DrawerLayout, TaskLayout } from '@layouts';

import { ENTITIES_PERMISIONS } from 'components/common/PermissionsGate/constants';
import { generateShortId, showError } from '@utils';
import { boardsServices } from '@services';
import { ButtonV2 } from 'components/common/ui';

import { TaskSection } from 'components/common/TaskSection';
import {
  ARNM_REPAIRS_TASK_FIELD_NAMES,
  ARNM_REPAIRS_TASK_STATUSES_NAMES,
} from 'components/arnmRepairs/constants';
import { useArnmRepairsTaskOperations } from '@hooks';

export const tabs = [
  {
    label: 'Ticket Details',
    id: 'ticket-details',
    icon: <InfoIcon data-testid="InfoIcon" />,
  },
  {
    label: 'Work Order',
    id: 'work-order',
    icon: <BuildIcon data-testid="BuildIcon" />,
  },
  {
    label: 'Financial Details',
    id: 'financial-details',
    icon: <EuroSymbolIcon data-testid="EuroSymbolIcon" />,
  },
  {
    label: 'Comments',
    id: 'comments',
    icon: <ChatBubbleIcon data-testid="ChatBubbleIcon" />,
  },
  {
    label: 'History',
    id: 'history',
    icon: <HistoryIcon data-testid="HistoryIcon" />,
  },
];

export const rightTabs = [
  {
    icon: <FormatListBulletedIcon data-testid="FormatListBulletedIcon" />,
    id: 'ticket-details-right',
  },
  {
    icon: <DirectionsCarIcon data-testid="DirectionsCarIcon" />,
    id: 'car-details',
  },
  {
    icon: <InsertDriveFileIcon data-testid="InsertDriveFileIcon" />,
    id: 'documents',
  },
  { icon: <InsertPhotoIcon data-testid="InsertPhotoIcon" />, id: 'images' },
];

const taskStatusNames = Object.keys(ARNM_REPAIRS_TASK_STATUSES_NAMES).reduce(
  (acc, cur) => {
    acc[cur] = ARNM_REPAIRS_TASK_STATUSES_NAMES[cur].label;

    return acc;
  },
  {}
);

const ArnmRepairsTask = () => {
  const router = useRouter();
  const [isSubmiting, setIsSubmitting] = useState(false);
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);

  const arnmRepairsTaskId = router.query?.arnmRepairsTaskId;
  const {
    task,
    assignees,
    arnmAssignees,
    refresh,
    mentionedUsers,
    vehicle,
    loading,
    stages,
    servicePoints,
    updateTask,
  } = useArnmRepairsTaskOperations(arnmRepairsTaskId);

  const submitBtnRef = useRef();

  const handleSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      await updateTask({ values, images, documents });
      await refresh();
      setImages([]);
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddComment = async (comment) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we save the comments
      await submitBtnRef?.current?.click?.();
      await boardsServices.addTaskComment(arnmRepairsTaskId, comment);

      await refresh();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, comment) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we edit the comments
      await submitBtnRef?.current?.click?.();
      await boardsServices.editTaskComment(
        arnmRepairsTaskId,
        commentId,
        comment
      );
      await refresh();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      setIsSubmitting(true);
      // we need to submit the form every time we delete the comments
      await submitBtnRef?.current?.click?.();
      await boardsServices.deleteTaskComment(arnmRepairsTaskId, commentId);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    try {
      setIsSubmitting(true);
      await boardsServices.deleteTaskImage(arnmRepairsTaskId, imageId);
      await refresh();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      setIsSubmitting(true);
      await boardsServices.deleteTaskDocument(arnmRepairsTaskId, docId);
      await refresh();
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeCarDetails = async ({ field, value }) => {
    const payload = {
      [field]: value,
    };

    try {
      setIsSubmitting(true);
      await boardsServices.updateTask(arnmRepairsTaskId, payload);

      await refresh();
      setImages([]);
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeStage = async (id) => {
    const payload = {
      status: Number(id),
    };
    try {
      setIsSubmitting(true);
      await submitBtnRef?.current?.click?.();
      await boardsServices.updateTask(arnmRepairsTaskId, payload);

      await refresh();
      setImages([]);
      setDocuments([]);
    } catch (error) {
      showError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderHeaderOptions = () => {
    return (
      <ButtonV2
        ref={submitBtnRef}
        type="submit"
        size="small"
        form="armrepairs-form"
        disabled={isSubmiting}
      >
        Save
      </ButtonV2>
    );
  };

  return (
    <>
      <Head>
        <title>{`Repairs/Maintenance: ${arnmRepairsTaskId} `}</title>
        <meta name="description" content="Instacar ledger" />
        <link rel="icon" href="/favicon.ico" />
      </Head>
      <TaskLayout
        task={arnmRepairsTaskId}
        entity={ENTITIES_PERMISIONS.ReadRepairs}
        renderHeaderOptions={renderHeaderOptions}
        isLoading={loading || isSubmiting}
        stages={stages}
        currentStage={task?.status?.value}
        onChangeStage={handleChangeStage}
      >
        <Grid container alignItems="start" spacing={1}>
          <Grid size={{ xs: 12, md: 8 }}>
            <TabsV2 tabs={tabs} observerRootId="arnmRepairs-task-details" />

            <TaskDetailScrollableSection id="arnmRepairs-task-details">
              <RepairsForm
                onSubmit={handleSubmit}
                defaultValues={task}
                loading={loading || isSubmiting}
                assignees={assignees}
                servicePoints={servicePoints}
                vehicleVin={vehicle?.vin}
              />

              <TaskSection title="Comments" id={tabs[3].id} icon={tabs[3].icon}>
                <CommentsV2
                  comments={task?.comments}
                  onSubmit={handleAddComment}
                  onEdit={handleEditComment}
                  onDelete={handleDeleteComment}
                  users={mentionedUsers}
                />
              </TaskSection>
              <TaskSection title="History" id={tabs[4].id} icon={tabs[4].icon}>
                <ActivityListV2
                  activities={task?.changelog}
                  taskFieldNames={ARNM_REPAIRS_TASK_FIELD_NAMES}
                  taskStatusNames={taskStatusNames}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TabsV2 tabs={rightTabs} observerRootId="arnmRepairs-task-right" />
            <TaskDetailScrollableSection id="arnmRepairs-task-right">
              <TaskSection
                isSmall
                title="Ticket Details"
                id={rightTabs[0].id}
                icon={rightTabs[0].icon}
              >
                <TaskCarDetails
                  task={task}
                  assignees={arnmAssignees}
                  stages={stages}
                  onChange={handleChangeCarDetails}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title="Car Details"
                id={rightTabs[1].id}
                icon={rightTabs[1].icon}
              >
                <TaskCarDetails
                  vehicle={vehicle}
                  onChange={handleChangeCarDetails}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title="Files"
                id={rightTabs[2].id}
                icon={rightTabs[2].icon}
              >
                <UploadARNMDocumentV2
                  key={`${generateShortId}`}
                  documents={task?.documents}
                  onChange={setDocuments}
                  onDeleteDocument={handleDeleteDocument}
                  loading={loading || isSubmiting}
                />
              </TaskSection>
              <TaskSection
                isSmall
                title="Images"
                id={rightTabs[3].id}
                icon={rightTabs[3].icon}
              >
                <UploadARNMImageV2
                  images={task?.images}
                  onChange={setImages}
                  onDeleteImage={handleDeleteImage}
                  loading={loading || isSubmiting}
                />
              </TaskSection>
            </TaskDetailScrollableSection>
          </Grid>
        </Grid>
      </TaskLayout>
    </>
  );
};

ArnmRepairsTask.getLayout = (page) => {
  return <DrawerLayout>{page}</DrawerLayout>;
};

export default ArnmRepairsTask;
